import React, { useEffect, useMemo, useState } from "react";
import { authFetch, getAuth, isAdmin } from "../../auth";
import { PermissionKeys } from "../../permissions";
import { usePermission } from "../Auth/PermissionContext";
import { ActionModal } from "../Common/ActionModal";

const TEMPLATE_OPTIONS = [
  { key: "readonly", label: "只读模板" },
  { key: "operator", label: "Operator 模板" },
  { key: "customer_admin", label: "CustomerAdmin 模板" },
  { key: "admin", label: "Admin 模板" }
];

function recommendTemplateByName(name) {
  const n = (name || "").trim().toLowerCase();
  if (n === "admin") return "admin";
  if (n === "customeradmin") return "customer_admin";
  if (n === "operator") return "operator";
  if (n === "default" || n === "readonly" || n === "all") return "readonly";
  return "";
}

function parsePermissionJson(raw) {
  if (!raw) return {};
  try {
    const obj = JSON.parse(raw);
    return obj && typeof obj === "object" ? obj : {};
  } catch {
    return {};
  }
}

function normalizePermissionObject(input) {
  const output = {};
  if (!input || typeof input !== "object") return output;
  Object.keys(input).forEach((k) => {
    if (input[k] === true || input[k] === false) {
      output[k] = input[k];
    }
  });
  return output;
}

function buildTemplateMap(templateKey, keys) {
  const grants = new Set();

  if (templateKey === "admin") {
    keys.forEach((k) => grants.add(k));
  } else if (templateKey === "customer_admin") {
    keys.forEach((k) => {
      if (k !== PermissionKeys.PermissionManage) {
        grants.add(k);
      }
    });
    grants.add(PermissionKeys.PermissionManage);
  } else if (templateKey === "operator") {
    [
      PermissionKeys.MonitorView,
      PermissionKeys.TrackView,
      PermissionKeys.DeviceView,
      PermissionKeys.DeviceShareView
    ].forEach((k) => grants.add(k));
  } else {
    [
      PermissionKeys.MonitorView,
      PermissionKeys.TrackView,
      PermissionKeys.DeviceView,
      PermissionKeys.DeviceShareView,
      PermissionKeys.UserView,
      PermissionKeys.GroupView,
      PermissionKeys.EmployeeView
    ].forEach((k) => grants.add(k));
  }

  const map = {};
  keys.forEach((k) => {
    map[k] = grants.has(k);
  });
  return map;
}

function formatAuditTime(input) {
  if (!input) return "-";
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("zh-CN", { hour12: false });
}

function getPermissionState(map, key) {
  if (!map || typeof map !== "object" || !(key in map)) return "默认";
  return map[key] === true ? "允许" : "拒绝";
}

export function PermissionManagement() {
  const admin = isAdmin();
  const auth = getAuth() || {};
  const { refresh } = usePermission();

  const [name, setName] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [keys, setKeys] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ id: null, customerId: "", name: "", remark: "", permissions: {} });
  const [templateKey, setTemplateKey] = useState("readonly");

  const [deleteModal, setDeleteModal] = useState({ open: false, row: null });
  const [modalSubmitting, setModalSubmitting] = useState(false);

  const [auditItems, setAuditItems] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const auditPageSize = 10;
  const [auditLoading, setAuditLoading] = useState(false);
  const [expandedAuditId, setExpandedAuditId] = useState(null);

  const pageCount = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total]);
  const auditPageCount = useMemo(() => Math.max(1, Math.ceil(auditTotal / auditPageSize)), [auditTotal]);

  useEffect(() => {
    loadPermissionKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    loadAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditPage]);

  useEffect(() => {
    if (!editing) return;
    const recommended = recommendTemplateByName(form.name);
    if (recommended) {
      setTemplateKey(recommended);
    }
  }, [editing, form.name]);

  async function loadPermissionKeys() {
    try {
      const res = await authFetch("/api/permissions/keys");
      if (!res.ok) return;
      const data = await res.json();
      setKeys(Array.isArray(data?.keys) ? data.keys : []);
    } catch {
      setKeys([]);
    }
  }

  async function loadConfigs(resetMsg = false) {
    setLoading(true);
    setError("");
    if (resetMsg) setMessage("");

    try {
      const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (name.trim()) qs.set("name", name.trim());
      if (admin && customerId.trim()) qs.set("customerId", customerId.trim());

      const res = await authFetch(`/api/permissions/configs?${qs.toString()}`);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "权限配置查询失败");
      }

      const data = await res.json();
      setItems(data?.items || []);
      setTotal(Number(data?.total || 0));
    } catch (err) {
      setError(err?.message || "权限配置查询失败");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  async function loadAudit() {
    setAuditLoading(true);
    try {
      const qs = new URLSearchParams({ page: String(auditPage), pageSize: String(auditPageSize) });
      if (name.trim()) qs.set("keyword", name.trim());
      if (admin && customerId.trim()) qs.set("customerId", customerId.trim());

      const res = await authFetch(`/api/permissions/audit?${qs.toString()}`);
      if (!res.ok) {
        setAuditItems([]);
        setAuditTotal(0);
        return;
      }

      const data = await res.json();
      setAuditItems(Array.isArray(data?.items) ? data.items : []);
      setAuditTotal(Number(data?.total || 0));
    } catch {
      setAuditItems([]);
      setAuditTotal(0);
    } finally {
      setAuditLoading(false);
    }
  }

  function onSearch(e) {
    e.preventDefault();
    setPage(1);
    setAuditPage(1);
    setTimeout(() => loadConfigs(true), 0);
    setTimeout(() => loadAudit(), 0);
  }

  function startCreate() {
    const cid = admin ? "" : String(auth?.customerId || "");
    setEditing("create");
    setTemplateKey("readonly");
    setForm({ id: null, customerId: cid, name: "", remark: "", permissions: {} });
  }

  function startEdit(row) {
    const recommended = recommendTemplateByName(row.name);
    if (recommended) {
      setTemplateKey(recommended);
    }
    setEditing("edit");
    setForm({
      id: row.id,
      customerId: String(row.customerId || ""),
      name: row.name || "",
      remark: row.remark || "",
      permissions: normalizePermissionObject(parsePermissionJson(row.permissions))
    });
  }

  function togglePermission(key, value) {
    setForm((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: value
      }
    }));
  }

  function applyTemplate() {
    if (keys.length === 0) return;
    const next = buildTemplateMap(templateKey, keys);
    setForm((prev) => ({ ...prev, permissions: next }));
  }

  async function saveConfig() {
    setError("");
    setMessage("");

    const cid = Number(form.customerId);
    if (!cid) {
      setError("客户ID不能为空。");
      return;
    }
    if (!form.name.trim()) {
      setError("配置名称不能为空。");
      return;
    }

    const payload = {
      id: form.id,
      customerId: cid,
      name: form.name.trim(),
      remark: form.remark.trim() || null,
      permissions: normalizePermissionObject(form.permissions)
    };

    const res = await authFetch("/api/permissions/configs", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      const txt = await res.text();
      setError(txt || "保存权限配置失败");
      return;
    }

    setMessage("权限配置已保存。已刷新当前权限。");
    setEditing(null);
    await loadConfigs();
    await loadAudit();
    await refresh();
  }

  function openDeleteModal(row) {
    setDeleteModal({ open: true, row });
  }

  async function submitDelete() {
    const row = deleteModal.row;
    if (!row) return;

    setModalSubmitting(true);
    setError("");
    setMessage("");

    try {
      const res = await authFetch(`/api/permissions/configs/${row.id}`, { method: "DELETE" });
      if (!res.ok) {
        const txt = await res.text();
        setError(txt || "删除配置失败");
        return;
      }

      setDeleteModal({ open: false, row: null });
      setMessage("权限配置已删除。已刷新当前权限。");
      await loadConfigs();
      await loadAudit();
      await refresh();
    } finally {
      setModalSubmitting(false);
    }
  }

  return (
    <div className="container-fluid">
      <div className="card shadow-sm status-card mt-3">
        <div className="card-header bg-primary text-white">权限管理</div>
        <div className="card-body">
          <form className="row g-2 align-items-end" onSubmit={onSearch}>
            <div className="col-md-3">
              <label className="form-label">配置名称</label>
              <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            {admin && (
              <div className="col-md-2">
                <label className="form-label">客户ID</label>
                <input className="form-control" value={customerId} onChange={(e) => setCustomerId(e.target.value)} />
              </div>
            )}
            <div className="col-md-2">
              <button className="btn btn-primary w-100" type="submit" disabled={loading}>{loading ? "查询中..." : "查询"}</button>
            </div>
            <div className="col-md-2">
              <button className="btn btn-success w-100" type="button" onClick={startCreate}>新增配置</button>
            </div>
          </form>

          {error && <div className="status-banner error mt-3">{error}</div>}
          {message && <div className="status-banner info mt-3">{message}</div>}

          <div className="table-responsive mt-3">
            <table className="table table-sm table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>客户</th>
                  <th>名称</th>
                  <th>备注</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {!loading && items.length === 0 && (
                  <tr><td colSpan={5}><div className="status-banner empty m-0">暂无权限配置</div></td></tr>
                )}
                {items.map((row) => (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>{row.customerName || `#${row.customerId}`}</td>
                    <td>{row.name}</td>
                    <td>{row.remark || "-"}</td>
                    <td>
                      <div className="d-flex gap-1 flex-wrap">
                        <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => startEdit(row)}>编辑</button>
                        <button className="btn btn-outline-danger btn-sm" type="button" onClick={() => openDeleteModal(row)}>删除</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="text-muted" style={{ fontSize: 13 }}>共 {total} 条，当前第 {page}/{pageCount} 页</div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary btn-sm" type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>上一页</button>
              <button className="btn btn-outline-secondary btn-sm" type="button" disabled={page >= pageCount || loading} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>下一页</button>
            </div>
          </div>

          <hr className="my-4" />
          <h6 className="mb-3">最近权限审计</h6>
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>时间</th>
                  <th>操作人</th>
                  <th>类型</th>
                  <th>目标</th>
                  <th>变更摘要</th>
                  <th>详情</th>
                </tr>
              </thead>
              <tbody>
                {!auditLoading && auditItems.length === 0 && (
                  <tr><td colSpan={6}><div className="status-banner empty m-0">暂无审计记录</div></td></tr>
                )}
                {auditItems.map((row) => (
                  <React.Fragment key={row.id}>
                    <tr>
                      <td>{formatAuditTime(row.opTime)}</td>
                      <td>{row.operatorName || "-"}</td>
                      <td>{row.opType}</td>
                      <td>{`#${row.targetCustomerId} / ${row.targetName}`}</td>
                      <td>
                        {row.changedCount > 0 ? (
                          <div className="d-flex gap-1 flex-wrap">
                            {(row.changedKeys || []).slice(0, 6).map((k) => (
                              <span key={k} className="badge bg-secondary">{k}</span>
                            ))}
                            {row.changedCount > 6 && (
                              <span className="badge bg-light text-dark">+{row.changedCount - 6}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">无权限变更</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          type="button"
                          onClick={() => setExpandedAuditId((id) => (id === row.id ? null : row.id))}
                        >
                          {expandedAuditId === row.id ? "收起" : "展开"}
                        </button>
                      </td>
                    </tr>
                    {expandedAuditId === row.id && (
                      <tr>
                        <td colSpan={6}>
                          {(row.changedKeys || []).length === 0 ? (
                            <div className="text-muted" style={{ fontSize: 13 }}>该记录没有可识别的权限点差异。</div>
                          ) : (
                            <div className="table-responsive">
                              <table className="table table-sm mb-0">
                                <thead>
                                  <tr>
                                    <th>权限点</th>
                                    <th>变更前</th>
                                    <th>变更后</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(row.changedKeys || []).map((k) => (
                                    <tr key={k}>
                                      <td>{k}</td>
                                      <td>{getPermissionState(row.before, k)}</td>
                                      <td>{getPermissionState(row.after, k)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="text-muted" style={{ fontSize: 13 }}>共 {auditTotal} 条，当前第 {auditPage}/{auditPageCount} 页</div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary btn-sm" type="button" disabled={auditPage <= 1 || auditLoading} onClick={() => setAuditPage((p) => Math.max(1, p - 1))}>上一页</button>
              <button className="btn btn-outline-secondary btn-sm" type="button" disabled={auditPage >= auditPageCount || auditLoading} onClick={() => setAuditPage((p) => Math.min(auditPageCount, p + 1))}>下一页</button>
            </div>
          </div>

          {editing && (
            <>
              <hr className="my-4" />
              <h6 className="mb-3">{editing === "create" ? "新增权限配置" : "编辑权限配置"}</h6>
              <div className="row g-2">
                <div className="col-md-2">
                  <label className="form-label">客户ID</label>
                  <input className="form-control" value={form.customerId} disabled={!admin} onChange={(e) => setForm((p) => ({ ...p, customerId: e.target.value }))} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">名称</label>
                  <input className="form-control" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="如 admin/customeradmin/operator/default" />
                </div>
                <div className="col-md-4">
                  <label className="form-label">备注</label>
                  <input className="form-control" value={form.remark} onChange={(e) => setForm((p) => ({ ...p, remark: e.target.value }))} />
                </div>
              </div>

              <div className="mt-3 border rounded p-2" style={{ background: "#fff" }}>
                <label className="form-label">模板一键应用</label>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <select className="form-select" style={{ width: 220 }} value={templateKey} onChange={(e) => setTemplateKey(e.target.value)}>
                    {TEMPLATE_OPTIONS.map((t) => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </select>
                  <button type="button" className="btn btn-outline-primary btn-sm" onClick={applyTemplate}>应用模板</button>
                  <small className="text-muted">名称识别：admin/customeradmin/operator/default。</small>
                  <small className="text-muted">模板会覆盖当前权限勾选状态。</small>
                </div>
              </div>

              <div className="mt-3">
                <label className="form-label">权限点配置</label>
                <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-2">
                  {keys.map((k) => {
                    const val = form.permissions?.[k];
                    return (
                      <div className="col" key={k}>
                        <div className="border rounded p-2" style={{ background: "#fff" }}>
                          <div className="fw-semibold" style={{ fontSize: 13 }}>{k}</div>
                          <div className="d-flex gap-2 mt-1">
                            <button type="button" className={`btn btn-sm ${val === true ? "btn-success" : "btn-outline-success"}`} onClick={() => togglePermission(k, true)}>允许</button>
                            <button type="button" className={`btn btn-sm ${val === false ? "btn-danger" : "btn-outline-danger"}`} onClick={() => togglePermission(k, false)}>拒绝</button>
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => {
                              const next = { ...(form.permissions || {}) };
                              delete next[k];
                              setForm((p) => ({ ...p, permissions: next }));
                            }}>默认</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="d-flex gap-2 mt-3">
                <button className="btn btn-primary" type="button" onClick={saveConfig}>保存配置</button>
                <button className="btn btn-outline-secondary" type="button" onClick={() => setEditing(null)}>取消</button>
              </div>
            </>
          )}
        </div>
      </div>

      <ActionModal
        open={deleteModal.open}
        title="删除权限配置"
        message={deleteModal.row ? `确认删除配置：${deleteModal.row.name} 吗？` : ""}
        fields={[]}
        initialValues={{}}
        confirmText="确认删除"
        confirmClassName="btn-danger"
        submitting={modalSubmitting}
        onCancel={() => setDeleteModal({ open: false, row: null })}
        onConfirm={submitDelete}
      />
    </div>
  );
}
